/**
 * Lot lifecycle — the reseller journey after the auction closes.
 *
 *   watching/bidding ──(auction closes)──► won ──► listed ──► sold
 *                                      └─► lost
 *
 * Statuses live on the lot document (lifecycleStatus, Phase 1 backend) with
 * outcome economics ({hammerPrice, feesPaid, salePrice, saleChannel, ...}).
 * This module derives effective status, computes cost basis / realized
 * profit, and applies transitions with optimistic update + remote sync.
 *
 * @module portal/modules/lifecycle
 */

import type { Lot, State } from '../types';
import { computeSpend } from '@lotlingo/shared';
import { resolveLotFees } from './lotOperations';
import { getSellChannelById } from './sellChannels';
import { syncLifecycleRemote } from './apiOperations/lifecycle';
import { save } from './state';
import { showToast } from './ui/toasts';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:lifecycle');

export type LifecycleStatus =
  | 'watching'
  | 'bidding'
  | 'won'
  | 'lost'
  | 'listed'
  | 'sold'
  | 'archived';

const VALID_STATUSES: ReadonlySet<string> = new Set([
  'watching',
  'bidding',
  'won',
  'lost',
  'listed',
  'sold',
  'archived',
]);

/** Effective lifecycle status: explicit value, else derived from bidding flag. */
export function getLifecycleStatus(lot: Lot | null | undefined): LifecycleStatus {
  const explicit = lot?.lifecycleStatus;
  if (typeof explicit === 'string' && VALID_STATUSES.has(explicit)) {
    return explicit as LifecycleStatus;
  }
  return lot?.bidding ? 'bidding' : 'watching';
}

/** True when the lot has been won (any post-win stage). */
export function isInventory(lot: Lot | null | undefined): boolean {
  const status = getLifecycleStatus(lot);
  return status === 'won' || status === 'listed';
}

/**
 * Cost basis for a won lot: hammer price + actual fees when recorded,
 * else hammer + fees estimated from the lot's resolved fee breakdown.
 * Null until a hammer price is known.
 */
export function computeCostBasis(lot: Lot, settings: unknown): number | null {
  const hammer = lot.outcome?.hammerPrice;
  if (typeof hammer !== 'number' || !Number.isFinite(hammer)) return null;
  const feesPaid = lot.outcome?.feesPaid;
  if (typeof feesPaid === 'number' && Number.isFinite(feesPaid)) {
    return Math.round((hammer + feesPaid) * 100) / 100;
  }
  // computeSpend = hammer + estimated tax/premium/fixed
  return computeSpend(hammer, resolveLotFees(lot, settings));
}

/**
 * Realized profit for a sold lot: sale price net of the sale channel's
 * selling costs, minus cost basis. Null until both sides are known.
 */
export function computeRealizedProfit(lot: Lot, settings: unknown): number | null {
  const costBasis = computeCostBasis(lot, settings);
  const salePrice = lot.outcome?.salePrice;
  if (costBasis == null || typeof salePrice !== 'number' || !Number.isFinite(salePrice)) {
    return null;
  }
  const channel = getSellChannelById(settings as never, lot.outcome?.saleChannel);
  const sellerCostsPct = channel ? channel.sellerCostsPct : 0;
  const netRevenue = salePrice * (1 - sellerCostsPct / 100);
  return Math.round((netRevenue - costBasis) * 100) / 100;
}

export interface OutcomePatch {
  hammerPrice?: number | null;
  feesPaid?: number | null;
  decidedAt?: string;
  salePrice?: number | null;
  saleChannel?: string;
  soldAt?: string;
}

export interface LifecycleDeps {
  state: State;
  render: () => void;
  ensureAccessToken: (retry?: boolean) => Promise<string | null>;
  mapRemoteDocument: (data: Record<string, unknown>) => Lot | null | undefined;
  applyRemoteSnapshot: (lots: Lot[]) => void;
}

const STATUS_TOAST: Record<string, string> = {
  won: 'Marked as won — it’s in your inventory now.',
  lost: 'Marked as lost.',
  listed: 'Marked as listed.',
  sold: 'Sale recorded. Check the Profit Dashboard for your realized P&L.',
  watching: 'Moved back to watching.',
};

/**
 * Apply a lifecycle transition (optionally with outcome data) to a lot:
 * optimistic local update, remote PATCH when signed in, rollback on failure.
 */
export async function applyLifecycleUpdate(
  lotId: string,
  status: LifecycleStatus,
  outcome: OutcomePatch | undefined,
  deps: LifecycleDeps
): Promise<void> {
  const { state, render, ensureAccessToken, mapRemoteDocument, applyRemoteSnapshot } = deps;
  const lot = state.lots.find(item => item && item.id === lotId);
  if (!lot) return;

  const previous = {
    lifecycleStatus: lot.lifecycleStatus,
    outcome: lot.outcome ? { ...lot.outcome } : lot.outcome,
    updatedAt: lot.updatedAt,
  };

  lot.lifecycleStatus = status;
  if (outcome) {
    lot.outcome = { ...(lot.outcome ?? {}), ...outcome };
  }
  lot.updatedAt = Date.now();
  render();

  const isRemote = state.mode === 'auth0' && !!lot.remote;
  if (!isRemote) {
    save();
    showToast(STATUS_TOAST[status] ?? 'Lot updated.', { variant: 'success', duration: 3000 });
    return;
  }

  try {
    const updated = await syncLifecycleRemote(
      lotId,
      { lifecycleStatus: status, ...(outcome ? { outcome } : {}) },
      state,
      ensureAccessToken,
      mapRemoteDocument
    );
    if (updated) {
      const index = state.lots.findIndex(item => item && item.id === updated.id);
      if (index >= 0) state.lots[index] = updated;
      applyRemoteSnapshot(state.lots);
      render();
    }
    showToast(STATUS_TOAST[status] ?? 'Lot updated.', { variant: 'success', duration: 3000 });
  } catch (err) {
    lot.lifecycleStatus = previous.lifecycleStatus;
    lot.outcome = previous.outcome;
    lot.updatedAt = previous.updatedAt;
    render();
    const message = err instanceof Error ? err.message : String(err);
    showToast('Failed to update lot status: ' + message, { variant: 'error', duration: 4000 });
    log.error('Lifecycle update failed', err);
  }
}
